import TextM from "@common/components/text/TextM"
import { HeaderContainerM } from "@common/header/HeaderContainerM"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { formIds } from "@data/constants/formConstants"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { resetUploadedFiles } from "@data/redux/actions/formData"
import { liveService } from "@data/services/liveService"
import { capitalize } from "@data/utility/Utils"
import { useNavigation } from "@react-navigation/native"
import { StatusBar } from "@common/systemBars/StatusBar"
import moment from "moment"
import React, { useContext, useEffect, useState } from "react"
import { SectionList, View } from "react-native"
import { ActivityIndicator, IconButton } from "react-native-paper"
import { connect } from "react-redux"
import SendTutorMessageModal from "./SendTutorMessageModal"
import StudentLiveCardComponent from "./StudentLiveCardComponent"
import LiveSessionsListWithJoin from "../../common/components/family/LiveSessionsListWithJoin"
import { parentOnlyBridge } from "../../featureFlags/screenBridge"

/**
 * Live sessions list (`live-sessions-list`).
 *
 * - mode parent → ports + DS `<LiveSessionsListPage>`
 * - élève et tuteur → legacy SectionList + StudentLiveCardComponent
 */

/**
 * Section header for date grouping
 */
const DateSectionHeader = ({ date }) => {
    const isToday = moment(date).isSame(moment(), "day")
    const isTomorrow = moment(date).isSame(moment().add(1, "day"), "day")

    let label
    if (isToday) {
        label = "Aujourd'hui"
    } else if (isTomorrow) {
        label = "Demain"
    } else {
        label = capitalize(moment(date).format("dddd D MMMM"))
    }

    return (
        <View
            style={{
                paddingVertical: 8,
                paddingHorizontal: 4,
                marginTop: 12,
            }}
        >
            <TextM
                fontWeight="Bold"
                fontSize={13}
                color={isToday ? theme.colors.success : theme.colors.grey}
            >
                {label.toUpperCase()}
            </TextM>
        </View>
    )
}

/**
 * Group occurrences by date for SectionList
 */
const groupOccurrencesByDate = (occurrences) => {
    if (!occurrences || occurrences.length === 0) return []

    const groups = {}
    occurrences.forEach((occurrence) => {
        const dateKey = moment(occurrence.date).format("YYYY-MM-DD")
        if (!groups[dateKey]) {
            groups[dateKey] = {
                date: occurrence.date,
                data: [],
            }
        }
        groups[dateKey].data.push(occurrence)
    })

    return Object.values(groups).sort((a, b) =>
        moment(a.date).diff(moment(b.date))
    )
}

const LegacyLiveSessionsListScreen = (props) => {
    const navigation = useNavigation()
    const dimensions = useContext(DimensionsContext)
    const formId = formIds.SEND_TUTOR_MESSAGE

    // Get student from route params (for parent mode / web portal)
    const studentEmail = props.route?.params?.student_email

    // Resolve the student user object:
    // - Parent mode: the app swaps sanctum tokens to the child, so the authed
    //   user already IS the target student.
    // - Admin mode: the student is typically in the backoffice users cache
    //   (after a user-detail lookup) or in loaded live_occurrences.
    // - Fallback stub so the parent flow still triggers before any of those
    //   sources resolve (race, deep link, etc.).
    const student = React.useMemo(() => {
        if (!studentEmail) return null
        const normalizedEmail = studentEmail.toLowerCase()
        const matchesEmail = (s) => s?.email?.toLowerCase() === normalizedEmail
        if (matchesEmail(props.user)) return props.user
        const fromOccurrences = props.liveOccurrences
            ?.flatMap((occ) => occ?.live_session?.students || [])
            ?.find(matchesEmail)
        if (fromOccurrences) return fromOccurrences
        const fromUsersList = props.usersList?.find(matchesEmail)
        if (fromUsersList) return fromUsersList
        return { email: studentEmail }
    }, [studentEmail, props.user, props.liveOccurrences, props.usersList])

    const [openSendTutorMessageModal, setOpenSendTutorMessageModal] =
        useState(false)
    const [sendTutorMessageTutor, setSendTutorMessageTutor] = useState(null)

    const handleShowSendTutorMessageModal = () =>
        setOpenSendTutorMessageModal(!openSendTutorMessageModal)

    const liveOccurrences = props.liveOccurrences

    const getRequest = !studentEmail
        ? liveService.getAllMyNextLiveSessions
        : liveService.getAllMyNextLiveSessionsAsParent
    const params = !studentEmail ? {} : { student_email: studentEmail }

    useEffect(() => {
        props.apiRequest(getRequest, {}, {}, params)
    }, [])

    useEffect(() => {
        props.resetUploadedFiles(formId)
    }, [formId])

    return (
        <View
            style={{
                flex: 1,
                backgroundColor: theme.colors.whiteAlmost,
            }}
        >
            <StatusBar
                style="dark"
                backgroundColor={theme.colors.white}
                translucent={false}
            />

            <HeaderContainerM
                style={{
                    justifyContent: "space-between",
                    paddingHorizontal: 10,
                    flexDirection: "row",
                    alignItems: "center",
                    backgroundColor: theme.colors.white,
                }}
            >
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                    <IconButton
                        icon={"times"}
                        onPress={() => {
                            if (navigation.canGoBack()) navigation.goBack()
                            else navigation.replace("menu")
                        }}
                        iconColor={theme.colors.primary}
                        size={32}
                    />
                    <TextM
                        fontWeight="Bold"
                        color={theme.colors.primary}
                        fontSize={20}
                    >
                        Tous mes lives
                    </TextM>
                </View>

                {props.getAllMyNextLiveSessionsRequest ||
                props.getAllMyNextLiveSessionsAsParentRequest ? (
                    <ActivityIndicator
                        color={theme.colors.primary}
                        size={"small"}
                    />
                ) : (
                    <IconButton
                        onPress={() => {
                            props.apiRequest(getRequest, {}, {}, params)
                        }}
                        iconColor={theme.colors.primary}
                        containerColor={theme.colors.transparent}
                        disabled={
                            props.getAllMyNextLiveSessionsRequest ||
                            props.getAllMyNextLiveSessionsAsParentRequest
                        }
                        icon={"sync-alt"}
                        size={20}
                    />
                )}
            </HeaderContainerM>

            {props.getAllMyNextLiveSessionsRequest ||
            props.getAllMyNextLiveSessionsAsParentRequest ? (
                <View
                    style={{
                        flex: 1,
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <ActivityIndicator
                        color={theme.colors.primary}
                        style={{ marginTop: 30 }}
                    />
                    <TextM wrap>{"Chargement..."}</TextM>
                </View>
            ) : liveOccurrences?.length === 0 ? (
                <View
                    style={{
                        flex: 1,
                        justifyContent: "center",
                        alignItems: "center",
                        paddingHorizontal: 20,
                    }}
                >
                    <TextM
                        fontWeight={"Light"}
                        fontSize={14}
                        wrap
                        style={{ textAlign: "center" }}
                    >
                        Aucune session planifiée
                    </TextM>
                </View>
            ) : (
                <SectionList
                    sections={groupOccurrencesByDate(liveOccurrences)}
                    keyExtractor={(occurrence) => occurrence.id.toString()}
                    renderSectionHeader={({ section }) => (
                        <DateSectionHeader date={section.date} />
                    )}
                    renderItem={({ item: occurrence }) => {
                        const liveSession = occurrence.live_session

                        return (
                            <StudentLiveCardComponent
                                liveSessionsForOneRoom={[liveSession]}
                                navigation={navigation}
                                liveSession={liveSession}
                                occurrence={occurrence}
                                style={{
                                    width: "100%",
                                }}
                                noButton={true}
                                subscriptionUser={student}
                                handleShowModal={() => {}}
                                handleShowSendTutorMessageModal={
                                    handleShowSendTutorMessageModal
                                }
                                dateTime={occurrence?.date}
                                sendTutorMessageTutor={sendTutorMessageTutor}
                                setSendTutorMessageTutor={
                                    setSendTutorMessageTutor
                                }
                            />
                        )
                    }}
                    contentContainerStyle={{
                        paddingHorizontal: 20,
                        paddingVertical: 10,
                        maxWidth: dimensions?.width,
                        alignSelf: "center",
                        width: "100%",
                    }}
                    showsVerticalScrollIndicator={false}
                    stickySectionHeadersEnabled={false}
                    initialNumToRender={10}
                />
            )}

            {openSendTutorMessageModal && (
                <SendTutorMessageModal
                    subscriptionUser={student || props.user}
                    isOpen={openSendTutorMessageModal}
                    handleShowSendTutorMessageModal={
                        handleShowSendTutorMessageModal
                    }
                    sendTutorMessageTutor={sendTutorMessageTutor}
                    setSendTutorMessageTutor={setSendTutorMessageTutor}
                    tutorIds={0}
                    formId={formId}
                />
            )}
        </View>
    )
}

const mapStateToProps = (state) => {
    return {
        getAllMyNextLiveSessionsRequest:
            state.api.getAllMyNextLiveSessions?.request,
        getAllMyNextLiveSessionsAsParentRequest:
            state.api.getAllMyNextLiveSessionsAsParent?.request,
        user: state.data.currentUser?.object,
        liveOccurrences: state.data.liveSessions?.live_occurrences,
        usersList: state.data.tutors?.usersList,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    resetUploadedFiles,
}

const ConnectedLegacyLiveSessionsListScreen = connect(
    mapStateToProps,
    mapDispatchToProps
)(LegacyLiveSessionsListScreen)

/**
 * Pont **mode parent seulement** : la route sert aussi l'élève et le tuteur, et l'écran du design
 * system a été dessiné pour le parent.
 */
export default parentOnlyBridge(
    LiveSessionsListWithJoin,
    ConnectedLegacyLiveSessionsListScreen
)
